import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { FileText, Target, Link2, Unlink, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  linkCarePlan, unlinkCarePlan, linkGoal, unlinkGoal,
  listCarePlansForParticipant, listGoalsForParticipant,
} from './reviewApi';

/**
 * Day 5 — Chart Linking Card
 *
 * Shows linked care plan + linked goal with manual link/change/unlink controls.
 * Fails safe: renders a null state (no controls) if reviewEnabled is false or
 * the feature flag is OFF server-side. All write actions require canEdit.
 * Read-only on archived charts.
 */

function LinkPicker({ open, onClose, title, options, emptyLabel, onPick, loading }) {
  const [selected, setSelected] = useState(null);
  useEffect(() => { if (!open) setSelected(null); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="link-picker-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto space-y-1.5 pr-1">
          {options.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="link-picker-empty">{emptyLabel}</p>
          ) : options.map((o) => (
            <button
              key={o.id}
              type="button"
              data-testid={`link-picker-option-${o.id}`}
              onClick={() => setSelected(o.id)}
              className={`w-full text-left p-2.5 rounded-md border text-sm transition-colors
                ${selected === o.id ? 'border-rose-400 bg-rose-50' : 'border-slate-200 hover:border-slate-300 hover:bg-muted/40'}`}
            >
              <div className="font-medium">{o.label}</div>
              {o.sublabel && <div className="text-[11px] text-muted-foreground">{o.sublabel}</div>}
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="link-picker-cancel">Cancel</Button>
          <Button
            size="sm"
            disabled={!selected || loading}
            onClick={() => selected && onPick(selected)}
            data-testid="link-picker-confirm"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Link2 className="w-3.5 h-3.5 mr-1" />}
            Link selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ChartLinkingCard({
  chart,
  participantId,
  canEdit,
  onChanged,
}) {
  const linkedCp = chart?.linked_care_plan || null;
  const linkedGoal = chart?.linked_goal || null;
  const isActive = chart?.status === 'active';
  const editable = canEdit && isActive;

  const [cpOpen, setCpOpen] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [cpOptions, setCpOptions] = useState([]);
  const [goalOptions, setGoalOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [busy, setBusy] = useState(false);

  const openCpPicker = async () => {
    setLoadingOptions(true);
    setCpOpen(true);
    const plans = await listCarePlansForParticipant(participantId);
    const mapped = plans
      .filter((p) => p.status && p.status !== 'ARCHIVED')
      .map((p) => ({
        id: p._id || p.id,
        label: p.title || p.plan_no || 'Care plan',
        sublabel: `${p.plan_no || ''}${p.status ? ` · ${p.status}` : ''}${p.version ? ` · v${p.version}` : ''}`,
      }));
    setCpOptions(mapped);
    setLoadingOptions(false);
  };

  const openGoalPicker = async () => {
    setLoadingOptions(true);
    setGoalOpen(true);
    const goals = await listGoalsForParticipant(participantId);
    const mapped = goals
      .filter((g) => g.status && g.status !== 'ARCHIVED')
      .map((g) => ({
        id: g._id || g.id,
        label: g.title || g.goal_no || 'Goal',
        sublabel: `${g.goal_no || ''}${g.category ? ` · ${g.category}` : ''}${g.status ? ` · ${g.status}` : ''}`,
      }));
    setGoalOptions(mapped);
    setLoadingOptions(false);
  };

  const pickCarePlan = async (id) => {
    try {
      setBusy(true);
      await linkCarePlan(participantId, chart.id, id);
      toast.success('Care plan linked');
      setCpOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Failed to link care plan');
    } finally { setBusy(false); }
  };

  const pickGoal = async (id) => {
    try {
      setBusy(true);
      await linkGoal(participantId, chart.id, id);
      toast.success('Goal linked');
      setGoalOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Failed to link goal');
    } finally { setBusy(false); }
  };

  const doUnlinkCarePlan = async () => {
    try {
      setBusy(true);
      await unlinkCarePlan(participantId, chart.id);
      toast.success('Care plan unlinked');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Failed to unlink');
    } finally { setBusy(false); }
  };

  const doUnlinkGoal = async () => {
    try {
      setBusy(true);
      await unlinkGoal(participantId, chart.id);
      toast.success('Goal unlinked');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Failed to unlink');
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="chart-linking-card">
      <CardContent className="p-4 space-y-3">
        {/* Care Plan row */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              <FileText className="w-3 h-3" /> Care plan
            </div>
            {linkedCp ? (
              <div className="mt-0.5" data-testid="linked-care-plan">
                <div className="text-sm font-medium truncate">{linkedCp.title || linkedCp.plan_no}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {linkedCp.plan_no && <Badge variant="outline" className="text-[10px]">{linkedCp.plan_no}</Badge>}
                  {linkedCp.status && <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-300 text-emerald-700">{linkedCp.status}</Badge>}
                </div>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground mt-0.5" data-testid="linked-care-plan-empty">No care plan linked</div>
            )}
          </div>
          {editable && (
            <div className="flex items-center gap-1.5 shrink-0">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openCpPicker} data-testid="link-care-plan-btn">
                {linkedCp ? <><Link2 className="w-3 h-3 mr-1" />Change</> : <><Plus className="w-3 h-3 mr-1" />Link</>}
              </Button>
              {linkedCp && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={doUnlinkCarePlan} disabled={busy} data-testid="unlink-care-plan-btn">
                  <Unlink className="w-3 h-3 mr-1" />Unlink
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Goal row */}
        <div className="flex items-start justify-between gap-3 pt-2 border-t">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              <Target className="w-3 h-3" /> Goal
            </div>
            {linkedGoal ? (
              <div className="mt-0.5" data-testid="linked-goal">
                <div className="text-sm font-medium truncate">{linkedGoal.title || linkedGoal.goal_no}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {linkedGoal.goal_no && <Badge variant="outline" className="text-[10px]">{linkedGoal.goal_no}</Badge>}
                  {linkedGoal.category && <Badge variant="outline" className="text-[10px]">{linkedGoal.category}</Badge>}
                  {linkedGoal.status && <Badge variant="outline" className="text-[10px] bg-blue-50 border-blue-300 text-blue-700">{linkedGoal.status}</Badge>}
                </div>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground mt-0.5" data-testid="linked-goal-empty">No goal linked</div>
            )}
          </div>
          {editable && (
            <div className="flex items-center gap-1.5 shrink-0">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openGoalPicker} data-testid="link-goal-btn">
                {linkedGoal ? <><Link2 className="w-3 h-3 mr-1" />Change</> : <><Plus className="w-3 h-3 mr-1" />Link</>}
              </Button>
              {linkedGoal && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={doUnlinkGoal} disabled={busy} data-testid="unlink-goal-btn">
                  <Unlink className="w-3 h-3 mr-1" />Unlink
                </Button>
              )}
            </div>
          )}
        </div>

        <LinkPicker
          open={cpOpen}
          onClose={() => setCpOpen(false)}
          title="Link a care plan"
          emptyLabel={loadingOptions ? 'Loading care plans…' : 'No care plans found for this participant'}
          options={cpOptions}
          onPick={pickCarePlan}
          loading={busy}
        />
        <LinkPicker
          open={goalOpen}
          onClose={() => setGoalOpen(false)}
          title="Link a goal"
          emptyLabel={loadingOptions ? 'Loading goals…' : 'No goals found for this participant'}
          options={goalOptions}
          onPick={pickGoal}
          loading={busy}
        />
      </CardContent>
    </Card>
  );
}

export default ChartLinkingCard;
